import { calculateDistance } from "../utils/geom"
import { Layer } from "./layer"
import { ol_Coordinate, ol_Feature, ol_geom } from "@omnia/exports/openlayers";

/**
 * Alternate version of "cleanupLayer" which also removes very small zones and inner rings
 */
export function cleanupAndRemoveSmallZones<T extends Layer & {
    readonly zones: { feature: ol_Feature }[]
    removeZones(zones: T["zones"])
}>(layer: T) {

    // Note: this is compared to areas calculated directly on geometries in the default EPSG:3857 projection,
    // as those areas are meaningless - 0.1 just means "very very small" here
    const minArea = 0.1

    const zonesToRemove = layer.zones.filter(z => {
        const g = z.feature?.getGeometry()
        if (g && g instanceof ol_geom.Polygon) {
            return (g.getArea() < minArea)
        }
    })

    if (zonesToRemove.length) {
        layer.removeZones(zonesToRemove)
    }

    const features = layer.getFeatures()

    features.forEach(f => cleanupFeature_(f, minArea))

}

export function cleanupLayer(layer: Layer) {

    cleanupFeatures(layer.getFeatures())

}

export function cleanupFeatures(features: ol_Feature[]) {

    features.forEach(f => cleanupFeature(f))

}

export function cleanupFeature(f: ol_Feature) {
    return cleanupFeature_(f)
}

function cleanupFeature_(f: ol_Feature, minimumRingArea?: number) {

    try {

        const geom = f.getGeometry()

        if (geom instanceof ol_geom.Polygon) {

            let poly = cleanupPolygon(geom)

            if (minimumRingArea > 0) {
                poly = removeSmallInnerRings(poly, minimumRingArea)
            }

            if (poly) {
                f.setGeometry(poly)
            }


        }


    } catch (error) {

        console.error("cleanupFeature", error)

    }

}

function removeSmallInnerRings(poly: ol_geom.Polygon, minArea: number) {

    let rings = poly.getLinearRings()

    const numRings = rings.length

    if (numRings <= 1) {
        return poly
    }

    rings = rings.filter(r => Math.abs(r.getArea()) >= minArea)

    if ((rings.length == 0) || (rings.length == numRings)) {
        return poly
    }

    const coords = rings.map(r => r.getCoordinates())

    return new ol_geom.Polygon(coords)

}

export function cleanupPolygon(poly: ol_geom.Polygon) {

    try {
        return cleanupPolygon_(poly)
    } catch (error) {
        console.error("cleanupPolygon", error)
    }

}

function cleanupPolygon_(poly: ol_geom.Polygon) {

    // 210615 - Lower threshold for the angle filter
    // const minAngle = 0.5 * (Math.PI / 180)
    const minAngle = 0.1 * (Math.PI / 180)

    // 210615 - The distance filter is disabled for now, produces invalid geometries in some cases
    // For instance: POLYGON((-238442.78672878156 7822449.32966095,-238442.78742376436 7822449.330678093,-238504.33208462436 7822528.459527756,-238504.32630636025 7822528.45209856,-238222.4495578347 7822501.859952474,-238349.45362215003 7822483.125724024,-238442.78672878156 7822449.32966095))
    // const minDistance = 0.01
    const minDistance = 0

    const rings = poly.getLinearRings()
        .map(ring => {
            const points = ring.getCoordinates().slice(0, -1)
            const fpoints = filterPoints(points)
            return fpoints
        })

    if (!rings[0]) return null

    const coords = rings.filter(r => !!r).map(r => r.slice().concat(r.slice(0, 1)))
    if (!coords.length) return null

    return new ol_geom.Polygon(coords)

    ////

    function filterPoints(points: ol_Coordinate[]) {

        const m = getPoints(points)

        const fpoints = m
            .filter(p => {
                if (p.dist < minDistance) return false
                if (p.angle < minAngle) return false
                return true
            })
            .map(p => p.point)

        // console.log("cleanupPolygons/filter", m.map(i => ({ x: i.point[0], y: i.point[1], angle: i.angle, dist: i.dist })))

        return (fpoints.length <= 2)
            ? null
            : (fpoints.length < points.length)
                ? filterPoints(fpoints)
                : fpoints

    }

    function getPoints(points: ol_Coordinate[]) {

        const distance = calculateDistance

        const last = points.length - 1

        return points.map((p1, index) => {

            const p0 = index == 0 ? points[last] : points[index - 1]
            const p2 = index == last ? points[0] : points[index + 1]

            const dist = distance(p1, p0)

            const va = Math.atan2(p0[1] - p1[1], p0[0] - p1[0])
            const vb = Math.atan2(p2[1] - p1[1], p2[0] - p1[0])
            const angle = Math.abs(vb - va)

            return {
                point: p1,
                dist: dist,
                angle: Math.min(angle, Math.abs(Math.PI - angle))
            }

        })

    }

}
